<script setup lang="ts">
/**
 * AI-пункты в меню редактора над выделением (DEV-1578).
 *
 * Встают в то же bubble-меню, что и «жирный» со «ссылкой» — через слот
 * `bubble-extra` дизайн-системы. Второго всплывающего меню рядом нет
 * намеренно: оно перекрывало бы первое, и человеку пришлось бы искать
 * форматирование и помощь в двух разных местах.
 *
 * Ghost-текста тоже нет: ответ модели показывается тем же сравнением «до и
 * после», что и остальные крупные действия ассистента, и в поле уходит только
 * подтверждённое. Пока диалог открыт, в задаче не меняется ничего — правка
 * ложится в модель поля, а сохраняет её человек кнопкой экрана, как обычно.
 */
import { AppButton, AppModal, useToast } from '@vinprop/design-system'
import { ListChecks, Sparkle, SquareStack } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import { inlineAssist, type InlineAction } from '@/features/assistant/api'
import DraftDiff from '@/features/assistant/DraftDiff.vue'
import { diffHunks } from '@/features/assistant/draft'

/**
 * Ровно то, что нужно от редактора: текст выделения. Тип структурный, а не
 * `Editor` из @tiptap/core — типтап здесь транзитивная зависимость дизайн-
 * системы, и тянуть её в импорты приложения ради одной сигнатуры не за чем.
 */
interface SelectionSource {
  state: {
    selection: { from: number; to: number }
    doc: { textBetween: (from: number, to: number, separator: string) => string }
  }
}

const props = defineProps<{
  /** Текущее содержимое поля в markdown: с ним сравнивают результат */
  value: string
  /** Редактор из слота: у него спрашивают выделение в момент нажатия */
  editor?: SelectionSource | null
  /** Откуда правят: гейт ai_enabled спрашивает про задачу или проект */
  issue?: string
  project?: string
}>()

/**
 * Выделение читается в момент нажатия, а не хранится реактивно: позиция курсора
 * живёт внутри ProseMirror и в реактивность Vue не заведена — сохранённая копия
 * отставала бы ровно на одно движение мыши.
 */
const selectionText = (): string => {
  const editor = props.editor
  if (!editor) return ''

  const { from, to } = editor.state.selection

  return from === to ? '' : editor.state.doc.textBetween(from, to, '\n')
}

const emit = defineEmits<{ 'update:value': [next: string] }>()

const { t } = useI18n()
const toast = useToast()

const busy = ref(false)
const action = ref<InlineAction | null>(null)
const answer = ref('')

/** Что получится в поле, если человек согласится. */
const next = computed(() =>
  answer.value ? `${props.value.trimEnd()}\n\n${answer.value}`.trimStart() : props.value,
)

const hunks = computed(() => diffHunks(props.value, next.value))

const title = computed(() =>
  action.value === 'subtasks' ? t('editor.ai.subtasks') : t('editor.ai.criteria'),
)

const run = async (next: InlineAction) => {
  if (busy.value) return

  busy.value = true

  try {
    answer.value = await inlineAssist({
      action: next,
      // Выделение точнее: человек показал, о чём спрашивает. Без него смотрим
      // на всё поле — «разбей на подзадачи» без выделения означает «это всё».
      text: selectionText().trim() || props.value,
      issue: props.issue,
      project: props.project,
    })

    action.value = next
  } catch (failure) {
    toast.error(errorMessage(failure, t('editor.ai.failed')))
  } finally {
    busy.value = false
  }
}

const apply = () => {
  emit('update:value', next.value)
  close()
}

const close = () => {
  action.value = null
  answer.value = ''
}
</script>

<template>
  <button
    type="button"
    class="eai__item"
    :disabled="busy"
    :title="t('editor.ai.criteria')"
    :aria-label="t('editor.ai.criteria')"
    @click="run('acceptance_criteria')"
  >
    <ListChecks :size="16" />
  </button>

  <button
    type="button"
    class="eai__item"
    :disabled="busy"
    :title="t('editor.ai.subtasks')"
    :aria-label="t('editor.ai.subtasks')"
    @click="run('subtasks')"
  >
    <SquareStack :size="16" />
  </button>

  <AppModal v-if="action" model-value :title="title" size="medium" @update:model-value="close">
    <p class="eai__note">
      <Sparkle :size="14" />
      {{ t('editor.ai.note') }}
    </p>

    <DraftDiff :hunks="hunks" />

    <template #footer>
      <AppButton variant="secondary" @click="close">{{ t('common.actions.cancel') }}</AppButton>
      <AppButton @click="apply">{{ t('editor.ai.insert') }}</AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
/* Кнопка того же роста, что и соседние в bubble-меню ДС. */
.eai__item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}

.eai__item:hover:not(:disabled) {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.eai__item:disabled {
  cursor: progress;
  opacity: 0.5;
}

.eai__note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}
</style>
